import {
  ComponentType,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { arrayMove } from '@dnd-kit/sortable';

import {
  ConnectionHandler,
  GraphQLTaggedNode,
  OperationType,
  PayloadError,
  RecordSourceProxy,
} from 'relay-runtime';
import { KeyTypeData, KeyType } from 'relay-runtime/lib/store/readInlineData';

import {
  fetchQuery,
  PreloadedQuery,
  useQueryLoader,
  useRefetchableFragment,
  useRelayEnvironment,
} from 'react-relay';

import { useCurrentUser } from 'components/CurrentUserProvider';
import { useNotification } from 'components/Notifications';

export function makeAfter(page: number, perPage: number) {
  const after = page * perPage;
  let buff = Buffer.from(after.toString());

  return buff.toString('base64');
}

type WithQueryComponentProps<Q extends OperationType> = {
  preloadedQuery: PreloadedQuery<Q>;
};

type VarFnArgs<R extends {} = {}> = {
  routeParams: R;
  searchParams: URLSearchParams;
  currentUser: ReturnType<typeof useCurrentUser>;
};

type WithQueryOptions<Q extends OperationType, R extends {} = {}> = {
  query: GraphQLTaggedNode;
  reloadOnRender?: boolean;
  loading?: ReactNode;
  variables?: (args: VarFnArgs<R>) => Q['variables'];
};

export function withData<Q extends OperationType, R extends {} = {}>(
  options: WithQueryOptions<Q, R>
) {
  return function (Component: ComponentType<WithQueryComponentProps<Q>>) {
    return function EntityComponent() {
      const { query, variables, loading, reloadOnRender = true } = options;
      const [loaded, setLoaded] = useState(false);
      const [searchParams] = useSearchParams();
      const [preloadedQuery, load] = useQueryLoader<Q>(query);
      const routeParams = useParams<any>();
      const currentUser = useCurrentUser();

      const vars = useMemo(
        () =>
          variables
            ? variables({ routeParams, currentUser, searchParams })
            : {},
        [currentUser, routeParams, searchParams, variables]
      );

      useEffect(() => {
        if (loaded && !reloadOnRender) return;

        load(vars);
        setLoaded(true);
      }, [load, loaded, reloadOnRender, vars]);

      return (
        <Suspense fallback={loading ?? 'Kraunasi...'}>
          {preloadedQuery && <Component preloadedQuery={preloadedQuery} />}
        </Suspense>
      );
    };
  };
}

export function deleteNode(
  store: RecordSourceProxy,
  rootID: string,
  connection: string,
  nodeID: string
) {
  const root = store.get(rootID);
  if (!root) return;

  const connectionRecord = ConnectionHandler.getConnection(root, connection);
  if (!connectionRecord) return;

  ConnectionHandler.deleteNode(connectionRecord, nodeID);
}

export function deleteRootConnectionNode(
  store: RecordSourceProxy,
  connectionID: string,
  nodeID: string
) {
  const connectionRecord = store.get(connectionID);
  if (!connectionRecord) return;

  ConnectionHandler.deleteNode(connectionRecord, nodeID);
}

export function swapEdges(
  store: RecordSourceProxy,
  connectionID: string,
  oldIndex: number,
  newIndex: number
) {
  const connectionRecord = store.get(connectionID);

  const edges = connectionRecord?.getLinkedRecords('edges') ?? [];

  const newEdges = arrayMove(edges, oldIndex, newIndex);

  connectionRecord?.setLinkedRecords(newEdges, 'edges');
}

type ErrorHandlerOptions<D> = {
  successMessage?: string | ((data: D) => string);
  onCompleted?: (res: D) => void;
  onError?: (error: Error) => void;
};

export function useErrorHandler<D>() {
  const { pop } = useNotification();

  return useMemo(
    () => (options?: ErrorHandlerOptions<D>) => {
      const { successMessage, onCompleted, onError } = options ?? {};

      return {
        onCompleted: (res: D, errors: PayloadError[] | null) => {
          if (errors) {
            errors.forEach(error => {
              pop(error.message);
            });
          } else if (successMessage) {
            pop(
              typeof successMessage === 'function'
                ? successMessage(res)
                : successMessage
            );
          }

          onCompleted && onCompleted(res);
        },

        onError: (error: any) => {
          const errors = error.source?.errors;
          if (errors) {
            errors.forEach((error: any) => {
              if (error.extensions.category === 'validation') {
                const validation = error.extensions.validation;

                Object.keys(validation).forEach(key => {
                  pop(validation[key]);
                });

                return;
              }

              pop(error.message);
            });

            return;
          }

          pop(error.message);
          onError && onError(error);
        },
      };
    },
    [pop]
  );
}

type AvoidSuspense<
  T extends OperationType = OperationType,
  K extends KeyType = KeyType
> = [
  data: KeyTypeData<K>,
  meta: {
    refetch: (variable: T['variables']) => void;
    loading: boolean;
  }
];

export function useAvoidSuspense<
  T extends OperationType = OperationType,
  K extends KeyType = KeyType
>(
  fragment: GraphQLTaggedNode,
  fragmentRef: K,
  query: GraphQLTaggedNode
): AvoidSuspense<T, K> {
  const [loading, setLoading] = useState(false);
  const environment = useRelayEnvironment();
  const { pop } = useNotification();
  const [data, refetchFragment] = useRefetchableFragment<T, K>(
    fragment,
    fragmentRef
  );

  const refetch = useCallback(
    (variables: T['variables']) => {
      setLoading(true);

      fetchQuery(environment, query, variables).subscribe({
        complete: () => {
          setLoading(false);
          refetchFragment(variables, { fetchPolicy: 'store-only' });
        },

        error: (error: any) => {
          if (error.source) {
            const errors = error.source.errors as PayloadError[];

            errors.forEach(error => {
              pop(error.message);
            });
          } else {
            pop(error.message);
          }

          setLoading(false);
        },
      });
    },
    [environment, pop, query, refetchFragment]
  );

  const meta = useMemo(() => ({ refetch, loading }), [loading, refetch]);

  return [data, meta];
}
